import { useQuery } from "@apollo/client";
import { ChevronDown, X } from "lucide-react";
import { useState } from "react";
import {
  formatDateRange,
  getFilterBadgeItems,
  hasActiveDateFilter,
  hasDateFilter,
  removeDateFilter,
  toggleDateFilter,
  toggleFilterState,
  formatWeightRange,
  hasWeightFilter,
  removeWeightFilter,
  toggleWeightFilter,
} from "./utils";
import { supplyChainLotBreedsQuery, getBreedOptions } from "@/benchmarks/BenchmarkFilters/BreedsPicker";
import {
  destinationCitiesQuery,
  getCityOptions as getDestinationCityOptions,
} from "@/benchmarks/BenchmarkFilters/DestinationCityPicker";
import { getDestinationOptions, lotDestinationsQuery } from "@/benchmarks/BenchmarkFilters/DestinationPicker";
import { destinationStateCodesQuery } from "@/benchmarks/BenchmarkFilters/DestinationStatePicker";
import { getFeedyardsOptions, supplyChainLotFeedyardsQuery } from "@/benchmarks/BenchmarkFilters/FeedyardPicker";
import { sexOptions } from "@/benchmarks/BenchmarkFilters/SexPicker";
import {
  sourceCitiesQuery,
  getCityOptions as getSourceCityOptions,
} from "@/benchmarks/BenchmarkFilters/SourceCityPicker";
import { getSourcesOptions, lotSourcesQuery } from "@/benchmarks/BenchmarkFilters/SourcePicker";
import { getStateOptions, sourceStateCodesQuery } from "@/benchmarks/BenchmarkFilters/SourceStatePicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { type BenchmarkFilters } from "@/routes/(authed)/_layout.benchmarks";

const GROUP_THRESHOLD = 5;

export type FilterBadgesProps = {
  filters: BenchmarkFilters;
  setFilters: React.Dispatch<React.SetStateAction<BenchmarkFilters>>;
  inactiveFilters: BenchmarkFilters;
  setInactiveFilters: React.Dispatch<React.SetStateAction<BenchmarkFilters>>;
  setSelectedBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
};

type FilterKey = keyof Pick<
  BenchmarkFilters,
  | "sexClass"
  | "animalBreedIds"
  | "lotDestinationIds"
  | "lotSourceIds"
  | "feedyardIds"
  | "sourceStateCodes"
  | "destinationStateCodes"
  | "sourceCities"
  | "destinationCities"
>;

export function FilterBadges({
  filters,
  setFilters,
  inactiveFilters,
  setInactiveFilters,
  setSelectedBenchmarkName,
}: FilterBadgesProps) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { data: breedsData, loading: loadingBreeds } = useQuery(supplyChainLotBreedsQuery);
  const { data: destinationsData, loading: loadingDestinations } = useQuery(lotDestinationsQuery);
  const { data: sourcesData, loading: loadingSources } = useQuery(lotSourcesQuery);
  const { data: feedyardsData, loading: loadingFeedyards } = useQuery(supplyChainLotFeedyardsQuery);
  const { data: sourceStatesData, loading: loadingSourceStates } = useQuery(sourceStateCodesQuery);
  const { data: destinationStatesData, loading: loadingDestinationStates } = useQuery(destinationStateCodesQuery);
  const { data: sourceCitiesData, loading: loadingSourceCities } = useQuery(sourceCitiesQuery, {
    variables: { businessUnitId: activeBusinessUnitId! },
    skip: !activeBusinessUnitId,
  });
  const { data: destinationCitiesData, loading: loadingDestinationCities } = useQuery(destinationCitiesQuery, {
    variables: { businessUnitId: activeBusinessUnitId! },
    skip: !activeBusinessUnitId,
  });

  const loading =
    loadingBreeds ||
    loadingDestinations ||
    loadingSources ||
    loadingFeedyards ||
    loadingSourceStates ||
    loadingDestinationStates ||
    loadingSourceCities ||
    loadingDestinationCities;

  const toggleFilter = (key: FilterKey, value: string) => {
    const result = toggleFilterState({
      value,
      activeFilters: filters[key],
      inactiveFilters: inactiveFilters[key],
    });

    setFilters({
      ...filters,
      [key]: result.activeFilters,
    });
    setInactiveFilters({
      ...inactiveFilters,
      [key]: result.inactiveFilters,
    });
  };

  const removeFilter = (key: FilterKey, value: string) => {
    setSelectedBenchmarkName("");

    setFilters({
      ...filters,
      [key]: filters[key]?.filter((v) => v !== value),
    });
    setInactiveFilters({
      ...inactiveFilters,
      [key]: inactiveFilters[key]?.filter((v) => v !== value),
    });
  };

  const allSexFilters = getFilterBadgeItems({
    options: sexOptions,
    activeFilters: filters.sexClass,
    inactiveFilters: inactiveFilters.sexClass,
  });

  const allFeedyardFilters = getFilterBadgeItems({
    options: getFeedyardsOptions(feedyardsData),
    activeFilters: filters.feedyardIds,
    inactiveFilters: inactiveFilters.feedyardIds,
  });

  const allBreedFilters = getFilterBadgeItems({
    options: getBreedOptions(breedsData),
    activeFilters: filters.animalBreedIds,
    inactiveFilters: inactiveFilters.animalBreedIds,
  });

  const destinationOptions = getDestinationOptions(destinationsData);
  const allDestinationsOptions = getFilterBadgeItems({
    options: destinationOptions,
    activeFilters: filters.lotDestinationIds,
    inactiveFilters: inactiveFilters.lotDestinationIds,
  });

  const sourceOptions = getSourcesOptions(sourcesData);
  const allSourcesOptions = getFilterBadgeItems({
    options: sourceOptions,
    activeFilters: filters.lotSourceIds,
    inactiveFilters: inactiveFilters.lotSourceIds,
  });

  const sourceStateOptions = getStateOptions(removeNothings(sourceStatesData?.supplyChainLotSourceStateCodes || []));
  const allSourceStateOptions = getFilterBadgeItems({
    options: sourceStateOptions.map((option) => ({
      ...option,
      label: `From: ${option.label}`,
    })),
    activeFilters: filters.sourceStateCodes,
    inactiveFilters: inactiveFilters.sourceStateCodes,
  });

  const destinationStateOptions = getStateOptions(
    removeNothings(destinationStatesData?.supplyChainLotDestinationStateCodes || []),
  );
  const allDestinationStateOptions = getFilterBadgeItems({
    options: destinationStateOptions.map((option) => ({
      ...option,
      label: `To: ${option.label}`,
    })),
    activeFilters: filters.destinationStateCodes,
    inactiveFilters: inactiveFilters.destinationStateCodes,
  });

  const sourceCityNodes = removeNothings(
    sourceCitiesData?.supplyChainLotSourceCities?.edges?.map((edge) => edge?.node) ?? [],
  );
  const sourceCityOptions = getSourceCityOptions(sourceCityNodes);
  const allSourceCityOptions = getFilterBadgeItems({
    options: sourceCityOptions.map((option) => ({
      ...option,
      label: `From: ${option.label}`,
    })),
    activeFilters: filters.sourceCities,
    inactiveFilters: inactiveFilters.sourceCities,
  });

  const destinationCityNodes = removeNothings(
    destinationCitiesData?.supplyChainLotDestinationCities?.edges?.map((edge) => edge?.node) ?? [],
  );
  const destinationCityOptions = getDestinationCityOptions(destinationCityNodes);
  const allDestinationCityOptions = getFilterBadgeItems({
    options: destinationCityOptions.map((option) => ({
      ...option,
      label: `To: ${option.label}`,
    })),
    activeFilters: filters.destinationCities,
    inactiveFilters: inactiveFilters.destinationCities,
  });

  if (loading) {
    return null;
  }

  const renderFilterGroup = (
    items: Array<{ value: string; label: string; active: boolean }>,
    filterKey: FilterKey,
    groupLabel: string,
  ): React.ReactNode => {
    if (items.length === 0) return null;

    if (items.length > GROUP_THRESHOLD) {
      const activeCount = items.filter((item) => item.active).length;
      const totalCount = items.length;
      const allActive = activeCount === totalCount;

      return (
        <GroupedFilterBadge
          key={filterKey}
          groupLabel={groupLabel}
          count={totalCount}
          enabled={allActive}
          items={items}
          onRemove={(value) => removeFilter(filterKey, value)}
          onToggle={(value) => toggleFilter(filterKey, value)}
          onRemoveAll={() => {
            setSelectedBenchmarkName("");
            const itemValues = items.map((item) => item.value);

            setFilters((prev) => ({
              ...prev,
              [filterKey]: prev[filterKey]?.filter((v) => !itemValues.includes(v)),
            }));

            setInactiveFilters((prev) => ({
              ...prev,
              [filterKey]: prev[filterKey]?.filter((v) => !itemValues.includes(v)),
            }));
          }}
        />
      );
    }

    return (
      <>
        {items.map(({ value, label, active }) => (
          <FilterBadge
            key={value}
            label={label}
            onRemove={() => removeFilter(filterKey, value)}
            onToggle={() => toggleFilter(filterKey, value)}
            enabled={active}
          />
        ))}
      </>
    );
  };

  return (
    <div data-testid="filter-badges-container" className="flex flex-wrap gap-1">
      {renderFilterGroup(allFeedyardFilters, "feedyardIds", "Feedyard")}
      {renderFilterGroup(allSexFilters, "sexClass", "Sex")}
      {renderFilterGroup(allBreedFilters, "animalBreedIds", "Breeds")}
      {renderFilterGroup(allDestinationsOptions, "lotDestinationIds", "Destination")}
      {renderFilterGroup(allSourcesOptions, "lotSourceIds", "Source")}
      {renderFilterGroup(allSourceStateOptions, "sourceStateCodes", "Source State")}
      {renderFilterGroup(allDestinationStateOptions, "destinationStateCodes", "Destination State")}
      {renderFilterGroup(allSourceCityOptions, "sourceCities", "Source City")}
      {renderFilterGroup(allDestinationCityOptions, "destinationCities", "Destination City")}
      {hasDateFilter(filters, inactiveFilters, "Opened") && (
        <FilterBadge
          label={formatDateRange(filters, inactiveFilters, "Opened")}
          onRemove={() => {
            setSelectedBenchmarkName("");
            const result = removeDateFilter(filters, inactiveFilters, "Opened");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
          enabled={hasActiveDateFilter(filters, "Opened")}
          onToggle={() => {
            const result = toggleDateFilter(filters, inactiveFilters, "Opened");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
        />
      )}
      {hasDateFilter(filters, inactiveFilters, "Closed") && (
        <FilterBadge
          label={formatDateRange(filters, inactiveFilters, "Closed")}
          onRemove={() => {
            setSelectedBenchmarkName("");
            const result = removeDateFilter(filters, inactiveFilters, "Closed");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
          enabled={hasActiveDateFilter(filters, "Closed")}
          onToggle={() => {
            const result = toggleDateFilter(filters, inactiveFilters, "Closed");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
        />
      )}
      {hasWeightFilter(filters, inactiveFilters) ? (
        <FilterBadge
          label={formatWeightRange(
            filters.weightAverageInMin || inactiveFilters.weightAverageInMin,
            filters.weightAverageInMax || inactiveFilters.weightAverageInMax,
          )}
          onRemove={() => {
            setSelectedBenchmarkName("");
            const result = removeWeightFilter(filters, inactiveFilters);
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
          enabled={Boolean(filters.weightAverageInMin || filters.weightAverageInMax)}
          onToggle={() => {
            const result = toggleWeightFilter(filters, inactiveFilters);
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
        />
      ) : null}
    </div>
  );
}

export function FilterBadge({
  label,
  onRemove,
  enabled,
  onToggle,
}: {
  label: string;
  onRemove: () => void;
  enabled: boolean;
  onToggle?: () => void;
}) {
  return (
    <Badge
      data-testid={`badge-${label.toLowerCase()}`}
      variant="default"
      className={`${!enabled ? "opacity-60" : ""} cursor-pointer`}
      onClick={onToggle && onToggle}
    >
      {label}
      <button
        type="button"
        className="ring-offset-background focus:ring-ring ml-1 rounded-full outline-none focus:ring-2 focus:ring-offset-2"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        aria-label={label}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">{label}</span>
      </button>
    </Badge>
  );
}

type GroupedFilterBadgeProps = {
  groupLabel: string;
  count: number;
  enabled: boolean;
  items: Array<{ value: string; label: string; active: boolean }>;
  onRemove: (value: string) => void;
  onToggle: (value: string) => void;
  onRemoveAll: () => void;
};

function GroupedFilterBadge({
  groupLabel,
  count,
  enabled,
  items,
  onRemove,
  onToggle,
  onRemoveAll,
}: GroupedFilterBadgeProps) {
  const [open, setOpen] = useState(false);

  const label = `${groupLabel} - ${count} selected`;

  const handleRemoveAll = () => {
    onRemoveAll();
    setOpen(false);
  };

  const handleRemove = (value: string) => {
    onRemove(value);
    if (items.length === 1) {
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Badge
          data-testid={`badge-grouped-${groupLabel.toLowerCase()}`}
          variant="default"
          className={`${!enabled ? "opacity-60" : ""} cursor-pointer`}
        >
          {label}
          <ChevronDown className="ml-1 h-3 w-3" />
          <button
            type="button"
            className="ring-offset-background focus:ring-ring ml-1 rounded-full outline-none focus:ring-2 focus:ring-offset-2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRemoveAll();
            }}
            aria-label={`Remove all ${groupLabel} filters`}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{`Remove all ${groupLabel} filters`}</span>
          </button>
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-h-[400px] overflow-y-auto" align="start">
        <ScrollArea className="h-full">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{groupLabel}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveAll}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear all
              </Button>
            </div>
            <Separator />
            <div className="flex flex-wrap gap-1">
              {items.map(({ value, label, active }) => (
                <FilterBadge
                  key={value}
                  label={label}
                  onRemove={() => handleRemove(value)}
                  onToggle={() => onToggle(value)}
                  enabled={active}
                />
              ))}
            </div>
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
